import { useEffect, useRef } from 'react'
import type { ReviewRating } from '@/types/review'

interface Options {
  revealed: boolean
  submitting: boolean
  /** True while another dialog (word detail) sits above the card. */
  nestedOpen: boolean
  onReveal: () => void
  onRate: (rating: ReviewRating) => void
}

const RATING_KEYS: Record<string, ReviewRating> = { '1': 1, '2': 2, '3': 3, '4': 4 }
const FIELD_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

function inFormField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (FIELD_TAGS.has(target.tagName)) return true
  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null
}

/**
 * Review-session key map (contracts/review-keyboard.md): Space/Enter reveal the
 * card front; 1–4 rate the revealed back. Keys are ignored inside form fields,
 * while a nested dialog is open, on auto-repeat, and after one rating has been
 * accepted for the current card. Mount once per card so the latch resets.
 */
export function useReviewKeyboard({ revealed, submitting, nestedOpen, onReveal, onRate }: Options) {
  const accepted = useRef(false)

  useEffect(() => {
    function handle(e: KeyboardEvent) {
      if (nestedOpen || e.repeat || inFormField(e.target)) return

      if (!revealed) {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault()
          onReveal()
        }
        return
      }

      const rating = RATING_KEYS[e.key]
      if (!rating || submitting || accepted.current) return
      accepted.current = true
      onRate(rating)
    }

    document.addEventListener('keydown', handle)
    return () => document.removeEventListener('keydown', handle)
  }, [revealed, submitting, nestedOpen, onReveal, onRate])
}
